import { displayChord, voicingKey, voicingsFor, type Notation } from '../../domain'
import { updatePreferences, usePreferences } from '../prefs'
import { t } from '../strings'
import { ChordDiagram } from './ChordDiagram'
import styles from './Play.module.css'

interface ChordCardProps {
  label: string
  symbol: string | undefined
  notation: Notation
  main?: boolean
}

/**
 * El acorde actual o el siguiente, con su nombre y su diagrama. Cada acorde puede tener varias
 * digitaciones: tocar el diagrama pasa a la siguiente y la elegida se recuerda (§3.3).
 */
export function ChordCard({ label, symbol, notation, main = false }: ChordCardProps) {
  const preferences = usePreferences()
  const name = symbol ? displayChord(symbol, notation, { unicode: true }) : '–'
  const voicings = symbol ? voicingsFor(symbol) : []
  const key = symbol ? voicingKey(symbol) : null
  const index = key ? Math.min(preferences.voicings[key] ?? 0, Math.max(0, voicings.length - 1)) : 0
  const voicing = voicings[index]

  const cycle = () => {
    if (!key || voicings.length < 2) return
    const next = (index + 1) % voicings.length
    updatePreferences((current) => ({ ...current, voicings: { ...current.voicings, [key]: next } }))
  }

  return (
    <div className={styles.chordCard}>
      <div>
        <div className={styles.chordCardLabel}>{label}</div>
        <div className={main ? styles.chordName : styles.nextName}>{name}</div>
      </div>
      <div className={styles.diagram}>
        {voicing ? (
          <button
            className={styles.diagramButton}
            onClick={cycle}
            disabled={voicings.length < 2}
            aria-label={`${name}. ${t.play.voicingOf(index + 1, voicings.length)}. ${t.play.voicingHint}`}
          >
            <ChordDiagram voicing={voicing} size={main ? 110 : 76} label={name} />
            {voicings.length > 1 && (
              <span className={styles.voicingCount}>
                {index + 1}/{voicings.length}
              </span>
            )}
          </button>
        ) : (
          symbol && <span className={styles.noDiagram}>{t.play.noDiagram}</span>
        )}
      </div>
    </div>
  )
}
